import {
  AREA_TYPES,
  AREA_TYPE_COLORS,
  AREA_TYPE_LABELS,
  DEFAULT_SERVICES_BY_TYPE,
  DEFAULT_PRODUCTION_RATES,
  LABOR_CATEGORIES,
  MODIFIER_MULTIPLIERS,
  SERVICES,
  resolveAreaDensity,
  resolveAreaType,
} from '../../SiteTakeoff/siteTakeoffConstants';

export const FREQUENCY_PRESETS = {
  weekly: { label: 'Weekly', visitsPerYear: 52, family: 'weekly' },
  biweekly: { label: 'Bi-weekly', visitsPerYear: 26, family: 'weekly' },
  monthly: { label: 'Monthly', visitsPerYear: 12, family: 'monthly' },
  bimonthly: { label: 'Bimonthly', visitsPerYear: 6, family: 'bimonthly' },
  quarterly: { label: 'Quarterly', visitsPerYear: 4, family: 'quarterly' },
  '3x-yr': { label: '3x / yr', visitsPerYear: 3, family: 'yr' },
  '2x-yr': { label: '2x / yr', visitsPerYear: 2, family: 'yr' },
  annual: { label: 'Annual', visitsPerYear: 1, family: 'yr' },
};

// Phoenix AZ seasonal heat productivity penalty on MAINTENANCE labor.
// Index 0=Jan ... 11=Dec. Peak summer slows the crew most; mild months no penalty.
const SEASONAL_HEAT_BY_MONTH = [
  1.0,  // Jan
  1.0,  // Feb
  1.0,  // Mar
  1.10, // Apr
  1.10, // May
  1.25, // Jun
  1.25, // Jul
  1.25, // Aug
  1.10, // Sep
  1.10, // Oct
  1.0,  // Nov
  1.0,  // Dec
];

export const heatFactorForMonth = (monthIndex) => {
  const m = Number(monthIndex);
  return Number.isInteger(m) && m >= 0 && m <= 11 ? SEASONAL_HEAT_BY_MONTH[m] : 1.0;
};

export const DEFAULT_ESTIMATE_SETTINGS = {
  minMarginPct: 45,
  // Phoenix seasonal heat productivity multiplier on MAINTENANCE labor only.
  // 1.0 = no penalty. The workspace overrides this with the current month's
  // value via heatFactorForMonth(); engine defaults keep unit tests at 1.0.
  seasonalHeatFactor: 1.0,
  laborRates: {
    Maint: 38.5,
    Arborist: 70,
    Herbicide: 75.5,
    Irrigation: 95.5,
    Operator: 75.5,
    'Crane Operator': 900,
  },
  productionRates: Object.entries(DEFAULT_PRODUCTION_RATES).reduce((acc, [key, rate]) => ({
    ...acc,
    [key]: {
      rate: rate.rate,
      visitsPerYear: rate.visitsPerYear,
      coveragePerVisit: rate.coveragePerVisit ?? 1,
    },
  }), {}),
  frequencies: FREQUENCY_PRESETS,
  modifierTable: {
    obstructed: MODIFIER_MULTIPLIERS.access_obstructed,
    sloped: MODIFIER_MULTIPLIERS.slope_sloped,
    'high-litter': MODIFIER_MULTIPLIERS.litterExposure_high,
    'med-weed': MODIFIER_MULTIPLIERS.weedPressure_med,
    'high-weed': MODIFIER_MULTIPLIERS.weedPressure_high,
    'limited-access': MODIFIER_MULTIPLIERS.limitedAccess,
    'blow-restrictions': MODIFIER_MULTIPLIERS.blowRestrictions,
    'hand-tools': 1.5,
  },
  irrigationZoneInference: {
    treesPerDripZone: 30,
    bedSFPerDripZone: 4000,
    flowerSFPerDripZone: 2500,
    turfSFPerSprayZone: 3500,
    backflowZonesPerSite: 1,
    minZonesPerSite: 3,
    minutesPerZone: 12,
  },
  subcontractors: {
    preEmergent: {
      vendorId: 'desert-spray',
      vendorName: 'Desert Spray Co.',
      unitRate: 400,
      unit: 'acre',
      minPerVisit: 500,
      frequency: '3x-yr',
      // Fraction of an area's mapped SF that counts toward sprayable acreage,
      // keyed by area type. Landscape & open gravel broadcast at full SF (1.0,
      // the default when a type is absent). Paved hardscape gets crack/edge
      // treatment only (~5%). Synthetic turf is excluded (0) — its pre-emergent
      // is a perimeter job handled outside per-acre broadcast. Tunable in Settings.
      coverageByType: {
        // Paved — cracks & edges only.
        as_parking: 0.05,
        hs_sidewalk: 0.05,
        hs_plaza: 0.05,
        pv_walkways: 0.05,
        hs_dumpster_pad: 0.05,
        // Synthetic turf — perimeter spray handled elsewhere, not broadcast.
        st_synthetic: 0,
      },
    },
  },
};

const ROLE_BY_CATEGORY = {
  mtc: 'Maint',
  arborist: 'Arborist',
  herbicide: 'Herbicide',
  irrigation: 'Irrigation',
};

const CATEGORY_BY_ROLE = Object.entries(ROLE_BY_CATEGORY)
  .reduce((acc, [category, role]) => ({ ...acc, [role]: category }), {});

const PRE_EMERGENT_TYPES = new Set(
  Object.entries(DEFAULT_SERVICES_BY_TYPE)
    .filter(([, services]) => Array.isArray(services) && services.includes(SERVICES.PRE_EMERGENT))
    .map(([areaType]) => areaType)
);

const TURF_TYPES = new Set([
  AREA_TYPES.NT_TURF_OPEN,
  AREA_TYPES.NT_TURF_OBSTRUCTED,
  AREA_TYPES.ST_SYNTHETIC,
]);

const FLOWER_TYPES = new Set([AREA_TYPES.BD_FLOWER_COLOR]);

const BED_TYPES = new Set([
  AREA_TYPES.GR_DESERT_LOW,
  AREA_TYPES.GR_DESERT_MED,
  AREA_TYPES.GR_DESERT_HIGH,
  AREA_TYPES.BD_SHRUB_LOW,
  AREA_TYPES.BD_SHRUB_MED,
  AREA_TYPES.BD_SHRUB_HIGH,
  AREA_TYPES.GC_GROUNDCOVER,
]);

const round = (value, places = 2) => {
  const factor = 10 ** places;
  return Math.round((Number(value) || 0) * factor) / factor;
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const getFrequency = (frequency, settings = DEFAULT_ESTIMATE_SETTINGS) => {
  if (frequency && typeof frequency === 'object') return frequency;
  return settings.frequencies?.[frequency] || FREQUENCY_PRESETS[frequency] || FREQUENCY_PRESETS.weekly;
};

export const getVisitsPerYear = (frequency, settings = DEFAULT_ESTIMATE_SETTINGS) =>
  safeNumber(getFrequency(frequency, settings).visitsPerYear, 0);

export const roleForCategory = (category) => ROLE_BY_CATEGORY[category] || category || 'Maint';

export const categoryForRole = (role) => CATEGORY_BY_ROLE[role] || role;

export const formatServiceLabel = (serviceCode) =>
  DEFAULT_PRODUCTION_RATES[serviceCode]?.label ||
  String(serviceCode || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const productionRateFor = (serviceCode, settings = DEFAULT_ESTIMATE_SETTINGS) => ({
  ...(DEFAULT_PRODUCTION_RATES[serviceCode] || {}),
  ...(settings.productionRates?.[serviceCode] || {}),
});

const frequencyFromVisits = (visitsPerYear) => {
  const visits = safeNumber(visitsPerYear, 52);
  const match = Object.entries(FREQUENCY_PRESETS)
    .find(([, preset]) => safeNumber(preset.visitsPerYear) === visits);
  return match?.[0] || { label: `${visits}x / yr`, visitsPerYear: visits, family: 'yr' };
};

export const modifierKeysFromArea = (area = {}) => {
  const modifiers = area.modifiers || {};
  const keys = [];
  if (modifiers.access === 'obstructed') keys.push('obstructed');
  if (modifiers.slope === 'sloped') keys.push('sloped');
  if (modifiers.litterExposure === 'high') keys.push('high-litter');
  if (modifiers.weedPressure === 'med') keys.push('med-weed');
  if (modifiers.weedPressure === 'high') keys.push('high-weed');
  if (modifiers.limitedAccess) keys.push('limited-access');
  if (modifiers.blowRestrictions) keys.push('blow-restrictions');
  return keys;
};

export function computeTask(task, modifierTable = DEFAULT_ESTIMATE_SETTINGS.modifierTable, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const modifierMultiplier = (task.modifiers || [])
    .reduce((product, key) => product * safeNumber(modifierTable[key], 1), 1);
  // Seasonal heat penalty applies to MAINTENANCE (mtc) tasks only. Unknown/absent
  // serviceCode -> no heat (safer to under-apply than to bump non-mtc work).
  const isMtc = DEFAULT_PRODUCTION_RATES[task.serviceCode]?.category === 'mtc';
  const heat = isMtc ? safeNumber(settings.seasonalHeatFactor, 1) : 1;
  const hoursPerVisit = task.ratePerHour > 0
    ? (safeNumber(task.quantity) / safeNumber(task.ratePerHour)) * safeNumber(task.coverage, 1) * modifierMultiplier * heat
    : 0;
  const visitsPerYear = getVisitsPerYear(task.frequency, settings);
  return {
    hoursPerVisit,
    annualHours: hoursPerVisit * visitsPerYear,
  };
}

export function computeService(service, settings = DEFAULT_ESTIMATE_SETTINGS) {
  if (!service?.enabled) {
    return { role: service?.role, hours: 0, cost: 0, perVisitCost: 0, visitsPerYear: 0 };
  }

  if (service.delivery === 'subcontracted') {
    const visitsPerYear = getVisitsPerYear(service.sub?.frequency, settings);
    const perVisitCost = Math.max(
      safeNumber(service.sub?.quantity) * safeNumber(service.sub?.unitRate),
      safeNumber(service.sub?.minPerVisit)
    );
    return {
      role: service.role,
      hours: 0,
      cost: perVisitCost * visitsPerYear,
      perVisitCost,
      visitsPerYear,
    };
  }

  const hours = (service.tasks || []).reduce((sum, task) => {
    const computed = task.hoursPerVisit != null && task.annualHours != null
      ? task
      : computeTask(task, settings.modifierTable, settings);
    return sum + safeNumber(computed.annualHours);
  }, 0);

  return { role: service.role, hours, cost: 0, perVisitCost: 0 };
}

export function dominantVisitsPerYear(estimate, tier, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const services = (estimate.services || [])
    .filter(service => service.enabled && tier.includedServices.includes(service.role));
  const visits = services.flatMap(service => {
    if (service.delivery === 'subcontracted') return [getVisitsPerYear(service.sub?.frequency, settings)];
    return (service.tasks || []).map(task => getVisitsPerYear(task.frequency, settings));
  }).filter(Boolean);
  return visits.length ? Math.max(...visits) : 12;
}

export function effectiveRate(role, estimate, tier, settings = DEFAULT_ESTIMATE_SETTINGS) {
  return safeNumber(
    tier.rateOverrides?.[role] ??
    estimate.rateOverrides?.[role] ??
    settings.laborRates?.[role],
    0
  );
}

// Human-readable subcontractor math for the trace ledger, e.g.
//   "Desert Spray Co. · 6 acre × $400 × 12/yr"  (rate-driven)
//   "Desert Spray Co. · $500 min/visit × 12/yr" (per-visit minimum applied)
// meta = { sub, perVisitCost, visitsPerYear } captured during accumulation.
function describeSubFormula(meta = {}) {
  const sub = meta.sub || {};
  const qty = safeNumber(sub.quantity);
  const unitRate = safeNumber(sub.unitRate);
  const minPerVisit = safeNumber(sub.minPerVisit);
  const perVisit = safeNumber(meta.perVisitCost);
  const visits = safeNumber(meta.visitsPerYear);
  const vendor = sub.vendorName ? `${sub.vendorName} · ` : '';
  const unit = sub.unit || 'unit';
  const fmt = (n) => '$' + Math.round(n).toLocaleString('en-US');
  // Min applies when the qty×rate product came in under the floor.
  const minApplied = minPerVisit > 0 && qty * unitRate < minPerVisit;
  const perVisitStr = minApplied
    ? `${fmt(minPerVisit)} min/visit`
    : `${(Math.round(qty * 100) / 100)} ${unit} × ${fmt(unitRate)}`;
  return `${vendor}${perVisitStr} × ${visits}/yr = ${fmt(perVisit * visits)}`;
}

export function computeTier(estimate, tier, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const hoursByRole = {};
  let laborCost = 0;
  let subCost = 0;
  // Per-role subcontract cost (labor cost is derived from hoursByRole below, so
  // subs — which contribute no hours — are captured here to trace them per role).
  const subCostByRole = {};
  // Per-role detail for the "Show the math" ledger: labor tasks (for the
  // Mowing/Trimming/Blowing breakdown) and the sub object + per-visit cost.
  const tasksByRole = {};
  const subMetaByRole = {};

  (estimate.services || []).forEach((service) => {
    if (!service.enabled || !tier.includedServices.includes(service.role)) return;
    const serviceResult = computeService(service, settings);
    if (service.delivery === 'subcontracted') {
      subCost += serviceResult.cost;
      subCostByRole[service.role] = (subCostByRole[service.role] || 0) + serviceResult.cost;
      subMetaByRole[service.role] = {
        sub: service.sub || {},
        perVisitCost: serviceResult.perVisitCost,
        visitsPerYear: serviceResult.visitsPerYear,
      };
      return;
    }
    hoursByRole[service.role] = (hoursByRole[service.role] || 0) + serviceResult.hours;
    (service.tasks || []).forEach((task) => {
      const computed = task.hoursPerVisit != null && task.annualHours != null
        ? task
        : computeTask(task, settings.modifierTable, settings);
      (tasksByRole[service.role] = tasksByRole[service.role] || []).push({
        label: task.label || formatServiceLabel(task.serviceCode),
        annualHours: safeNumber(computed.annualHours),
      });
    });
  });

  Object.entries(hoursByRole).forEach(([role, hours]) => {
    laborCost += hours * effectiveRate(role, estimate, tier, settings);
  });

  const totalCost = laborCost + subCost;
  const marginPct = Math.min(95, Math.max(0, safeNumber(tier.marginPct)));
  const annualPrice = marginPct < 100 ? totalCost / (1 - marginPct / 100) : 0;
  const visitsPerYear = dominantVisitsPerYear(estimate, tier, settings);

  // Per-role trace ("Show the math" ledger). One line per included role at the
  // exact granularity computeTier prices — so annualCost sums to totalCost.
  // These rates are default-sourced (no benchmark/measured provenance here),
  // stamped honestly so the CalcTracePanel badge reads "default".
  const trace = [];
  Object.entries(hoursByRole).forEach(([role, hours]) => {
    const rate = effectiveRate(role, estimate, tier, settings);
    // Per-task breakdown (Mowing/Trimming/Blowing…) priced at the role's rate,
    // so the sub-lines sum to the role line's annualCost.
    const tasks = tasksByRole[role] || [];
    const subLines = tasks.length > 1
      ? tasks.map((t) => ({
          label: t.label,
          annualHours: t.annualHours,
          annualCost: t.annualHours * rate,
        }))
      : undefined;
    trace.push({
      areaLabel: categoryForRole(role),
      service: role,
      rawQuantity: hours,
      effectiveQuantity: hours,
      effectiveUnit: 'hrs/yr',
      productionRate: rate,
      annualHours: hours,
      annualCost: hours * rate,
      provenance: { source: 'default' },
      ...(subLines ? { subLines } : {}),
    });
  });
  Object.entries(subCostByRole).forEach(([role, cost]) => {
    const meta = subMetaByRole[role] || {};
    trace.push({
      areaLabel: categoryForRole(role),
      service: role,
      rawQuantity: 0,
      effectiveQuantity: 0,
      effectiveUnit: 'subcontracted',
      productionRate: 0,
      annualHours: 0,
      annualCost: cost,
      provenance: { source: 'default' },
      subFormula: describeSubFormula(meta),
    });
  });

  return {
    hoursByRole,
    laborCost,
    subCost,
    totalCost,
    annualPrice,
    monthlyPrice: annualPrice / 12,
    perVisitPrice: visitsPerYear > 0 ? annualPrice / visitsPerYear : 0,
    effectiveMargin: marginPct,
    trace,
  };
}

export function solveMarginForTarget(estimate, tier, settings = DEFAULT_ESTIMATE_SETTINGS, target = tier.targetPrice) {
  if (!target || safeNumber(target.amount) <= 0) return null;
  const tierCost = computeTier(estimate, { ...tier, targetPrice: undefined }, settings).totalCost;
  const annualTarget = target.basis === 'per-visit'
    ? safeNumber(target.amount) * dominantVisitsPerYear(estimate, tier, settings)
    : safeNumber(target.amount) * 12;
  if (annualTarget <= 0) return null;
  return round((1 - tierCost / annualTarget) * 100, 1);
}

export function runSanityChecks(estimate, settings = DEFAULT_ESTIMATE_SETTINGS, portfolioStats = {}) {
  const laborDriftOk = Object.entries(estimate.rateOverrides || {}).every(([role, rate]) => {
    const base = safeNumber(settings.laborRates?.[role]);
    if (!base) return true;
    return Math.abs(safeNumber(rate) - base) / base <= 0.05;
  });

  const marginsOk = (estimate.tiers || []).every(tier => safeNumber(tier.marginPct) >= safeNumber(settings.minMarginPct, 45));
  const mappedOk = !safeNumber(estimate.unmappedAreaCount, 0);
  const lowCoverageTasks = (estimate.services || []).flatMap(service => service.tasks || [])
    .filter(task => safeNumber(task.coverage, 1) < 0.2 && !task.coverageConfirmed);
  const highFrequencyTasks = (estimate.services || []).flatMap(service => service.tasks || [])
    .filter((task) => {
      const median = safeNumber(portfolioStats?.serviceMedianAnnualHours?.[task.serviceCode]);
      if (!median) return false;
      const annual = safeNumber(task.annualHours ?? computeTask(task, settings.modifierTable, settings).annualHours);
      return annual > median * 3;
    });

  return [
    {
      key: 'rates',
      status: laborDriftOk ? 'pass' : 'warn',
      label: 'Hourly rates within +/-5% of company defaults',
    },
    {
      key: 'margin',
      status: marginsOk ? 'pass' : 'warn',
      label: `Margin meets ${settings.minMarginPct || 45}% minimum threshold`,
    },
    {
      key: 'mapping',
      status: mappedOk ? 'pass' : 'warn',
      label: 'All scope areas mapped and assigned a service',
    },
    {
      key: 'frequency',
      status: lowCoverageTasks.length === 0 && highFrequencyTasks.length === 0 ? 'pass' : 'warn',
      label: lowCoverageTasks.length
        ? 'Coverage below 20% needs confirmation before sending'
        : 'Low Pruning frequency is unusually high - confirm before sending',
    },
  ];
}

const getAreas = (takeoffOrAreas) => {
  if (Array.isArray(takeoffOrAreas)) return takeoffOrAreas;
  return takeoffOrAreas?.areas || [];
};

const areaSqft = (area) => Math.max(0, safeNumber(area.sqft ?? area.area));

// L2: palmCount was previously read here but never existed in the schema
// (Mongoose strips unknown fields — verified). The correct source for palm/tree
// split is treeDetails.species in serviceFlavorsModel.seedFromTakeoff. For drip
// zone inference (inferIrrigationZones), we count ALL trees regardless of species
// since both palms and trees need drip circuits.
const areaTreeCount = (area) =>
  safeNumber(area.treeCount ?? area.plantDensity?.treeCount);

const densityFactor = (density) => ({
  none: 0.05,
  low: 0.2,
  med: 0.45,
  high: 0.75,
}[density] ?? 0.2);

export function inferIrrigationZones(takeoff, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const allAreas = getAreas(takeoff);
  const cfg = settings.irrigationZoneInference || DEFAULT_ESTIMATE_SETTINGS.irrigationZoneInference;

  // Build exclusion-child SF totals keyed by parent area id (mirrors laborCalculator netting).
  const exclusionSFByParent = {};
  allAreas.forEach((a) => {
    if (!a.parentAreaId) return;
    const pid = typeof a.parentAreaId === 'object' ? a.parentAreaId.toString() : String(a.parentAreaId);
    exclusionSFByParent[pid] = (exclusionSFByParent[pid] || 0) + areaSqft(a);
  });

  const areas = allAreas.filter(area => !area.parentAreaId);

  let trees = 0;
  let bedSF = 0;
  let flowerSF = 0;
  let turfSF = 0;

  areas.forEach((area) => {
    const type = resolveAreaType(area.areaType);
    const grossSqft = areaSqft(area);
    const rawId = area._id || area.id;
    const areaId = rawId ? (typeof rawId === 'object' ? rawId.toString() : String(rawId)) : null;
    const excSF = areaId ? (exclusionSFByParent[areaId] || 0) : 0;
    // Net SF after exclusion children; clamp at zero (mirrors laborCalculator).
    const sqft = Math.max(0, grossSqft - excSF);
    // Count all trees (palms + non-palms) for drip-zone inference.
    trees += areaTreeCount(area);
    if (FLOWER_TYPES.has(type)) flowerSF += safeNumber(area.flowerSF, sqft);
    if (TURF_TYPES.has(type)) turfSF += safeNumber(area.turfSF, sqft);
    if (BED_TYPES.has(type)) {
      const explicit = safeNumber(area.shrubSF) + safeNumber(area.groundcoverSF);
      // Density resolves the same way as the labor calculator: manual dropdown
      // (low/med/high) wins, else the area type's own Low/Med/High implies it.
      bedSF += explicit || sqft * densityFactor(resolveAreaDensity(area));
    }
  });

  const treeZones = Math.ceil(trees / safeNumber(cfg.treesPerDripZone, 30));
  const bedZones = Math.ceil(bedSF / safeNumber(cfg.bedSFPerDripZone, 4000));
  const flowerZones = Math.ceil(flowerSF / safeNumber(cfg.flowerSFPerDripZone, 2500));
  const turfZones = Math.ceil(turfSF / safeNumber(cfg.turfSFPerSprayZone, 3500));
  const backflowZones = safeNumber(cfg.backflowZonesPerSite, 1);
  const rawZones = treeZones + bedZones + flowerZones + turfZones + backflowZones;
  const totalZones = Math.max(rawZones, safeNumber(cfg.minZonesPerSite, 3));

  return {
    breakdown: [
      { label: `${round(trees, 0)} trees / palms`, color: '#7a3a2e', quantity: treeZones, math: `/${cfg.treesPerDripZone} per zone` },
      { label: `${round(bedSF, 0).toLocaleString()} SF shrubs / groundcover`, color: '#d18227', quantity: bedZones, math: `/${cfg.bedSFPerDripZone} SF per zone` },
      { label: `${round(flowerSF, 0).toLocaleString()} SF flowers`, color: '#ec407a', quantity: flowerZones, math: `/${cfg.flowerSFPerDripZone} SF per zone` },
      { label: `${round(turfSF, 0).toLocaleString()} SF turf`, color: '#6f9f4e', quantity: turfZones, math: `/${cfg.turfSFPerSprayZone} SF per zone` },
      { label: 'Backflow + master valve check', color: '#cec3a5', quantity: backflowZones, math: 'site allowance' },
    ],
    totalZones,
    rawZones,
    minutesPerZone: safeNumber(cfg.minutesPerZone, 12),
  };
}

export function inferPreEmergentAcreage(takeoff, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const allAreas = getAreas(takeoff);

  // Build exclusion-child SF totals keyed by parent area id (mirrors laborCalculator netting).
  const exclusionSFByParent = {};
  allAreas.forEach((a) => {
    if (!a.parentAreaId) return;
    const pid = typeof a.parentAreaId === 'object' ? a.parentAreaId.toString() : String(a.parentAreaId);
    exclusionSFByParent[pid] = (exclusionSFByParent[pid] || 0) + areaSqft(a);
  });

  const areas = allAreas.filter(area => !area.parentAreaId);
  const buckets = {};
  let totalSF = 0;

  const sub = settings.subcontractors?.preEmergent || DEFAULT_ESTIMATE_SETTINGS.subcontractors.preEmergent;
  // Per-type fraction of mapped SF that is actually sprayable. Absent type -> 1.0
  // (full broadcast). Paved hardscape is ~0.05 (crack/edge); synthetic turf 0.
  const coverageByType = sub.coverageByType || {};
  // A type is "explicitly" configured when it carries a coverage entry — that is
  // the signal that sweeps paved hardscape (not in PRE_EMERGENT_TYPES) into the
  // acreage. Types with no entry default to 1.0 but are NOT auto-included; they
  // must earn inclusion the usual way (PRE_EMERGENT_TYPES or the per-area toggle),
  // so unclassified / untyped areas stay out (M3 guarantee).
  const hasCoverageEntry = (type) => typeof coverageByType[type] === 'number';
  const coverageFor = (type) => (hasCoverageEntry(type) ? coverageByType[type] : 1);

  areas.forEach((area) => {
    const type = resolveAreaType(area.areaType);
    const fraction = coverageFor(type);
    // Gate: an explicit per-area toggle wins; otherwise include when the type
    // defaults to pre-emergent OR carries an explicit coverage entry (paved
    // hardscape). A fraction of 0 (e.g. synthetic turf) is never included.
    const include = typeof area.preEmergentEnabled === 'boolean'
      ? area.preEmergentEnabled
      : (PRE_EMERGENT_TYPES.has(type) || hasCoverageEntry(type));
    if (!include || fraction <= 0) return;

    const rawId = area._id || area.id;
    const areaId = rawId ? (typeof rawId === 'object' ? rawId.toString() : String(rawId)) : null;
    const excSF = areaId ? (exclusionSFByParent[areaId] || 0) : 0;
    // Net SF after exclusion children; clamp at zero (mirrors laborCalculator).
    const grossSqft = Math.max(0, areaSqft(area) - excSF);
    // Sprayable SF = gross × coverage fraction. Drives acreage & cost; the bucket
    // shows gross (mapped) SF for orientation plus the sprayable figure.
    const sprayableSqft = grossSqft * fraction;

    totalSF += sprayableSqft;
    if (!buckets[type]) {
      buckets[type] = {
        label: AREA_TYPE_LABELS[type] || type || 'Mapped area',
        color: AREA_TYPE_COLORS[type] || '#cec3a5',
        areas: 0,
        sqft: 0,        // sprayable SF (post-fraction) — feeds the acreage total
        grossSF: 0,     // mapped SF before the coverage fraction
        coverage: fraction,
      };
    }
    buckets[type].areas += 1;
    buckets[type].sqft += sprayableSqft;
    buckets[type].grossSF += grossSqft;
  });

  const totalAcres = totalSF / 43560;
  const perVisitCost = Math.max(totalAcres * safeNumber(sub.unitRate), safeNumber(sub.minPerVisit));
  const minApplied = perVisitCost === safeNumber(sub.minPerVisit) && totalAcres * safeNumber(sub.unitRate) < safeNumber(sub.minPerVisit);

  return {
    breakdown: Object.values(buckets),
    totalSF,
    totalAcres,
    perVisitCost,
    annualCost: perVisitCost * getVisitsPerYear(sub.frequency, settings),
    minApplied,
  };
}

export function buildServiceLinesFromTakeoff(areas, hoursResult, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const preEmergent = inferPreEmergentAcreage(areas, settings);
  const irrigation = inferIrrigationZones(areas, settings);

  // Seasonal heat penalty applies to MAINTENANCE labor only (the crew doing
  // physical work in the sun); other categories multiply by 1 (no bump).
  const heatFactor = safeNumber(settings.seasonalHeatFactor, 1);

  const serviceLines = Object.entries(LABOR_CATEGORIES).map(([category, meta]) => {
    const role = roleForCategory(category);
    const lineHeat = category === 'mtc' ? heatFactor : 1;
    const categoryHours = hoursResult?.byCategory?.[category] || {};
    const routineTasks = Object.entries(hoursResult?.routineByService || {})
      .filter(([, task]) => task.category === category)
      .map(([serviceCode, task]) => {
        const rateDefinition = productionRateFor(serviceCode, settings);
        const visitsPerYear = safeNumber(task.visitsPerYear ?? rateDefinition.visitsPerYear, 52);
        const baseHpv = safeNumber(task.totalHoursPerApplication ?? task.hoursPerApplication, 0);
        const baseAnnual = safeNumber(task.annualHours, baseHpv * visitsPerYear);
        return {
          serviceCode,
          label: formatServiceLabel(serviceCode),
          areaCount: task.areaCount,
          ratePerHour: safeNumber(rateDefinition.rate, 1),
          quantity: 0,
          coverage: task.coveragePerVisit ?? rateDefinition.coveragePerVisit ?? 1,
          frequency: frequencyFromVisits(visitsPerYear),
          hoursPerVisit: baseHpv * lineHeat,
          annualHours: baseAnnual * lineHeat,
        };
      });
    const periodicTasks = (categoryHours.periodicServices || []).map((task) => {
      const rateDefinition = productionRateFor(task.service, settings);
      const visitsPerYear = safeNumber(task.visitsPerYear ?? rateDefinition.visitsPerYear, 0);
      const baseHpv = safeNumber(task.hoursPerApplication, 0);
      return {
        serviceCode: task.service,
        label: formatServiceLabel(task.service),
        areaCount: task.areaCount,
        ratePerHour: safeNumber(rateDefinition.rate, 1),
        quantity: 0,
        coverage: task.coveragePerVisit ?? rateDefinition.coveragePerVisit ?? 1,
        frequency: frequencyFromVisits(visitsPerYear),
        hoursPerVisit: baseHpv * lineHeat,
        annualHours: safeNumber(task.annualHours, baseHpv * visitsPerYear) * lineHeat,
      };
    });
    return {
      role,
      name: meta.label,
      enabled: (categoryHours.annualTotal || 0) > 0,
      delivery: 'labor',
      routineTasks,
      periodicTasks,
      tasks: [...routineTasks, ...periodicTasks],
    };
  });

  const herbicideSub = settings.subcontractors?.preEmergent || DEFAULT_ESTIMATE_SETTINGS.subcontractors.preEmergent;
  if (preEmergent.totalAcres > 0) {
    const herbIndex = serviceLines.findIndex(line => line.role === 'Herbicide');
    serviceLines[herbIndex] = {
      role: 'Herbicide',
      name: 'Herbicide',
      enabled: true,
      delivery: 'subcontracted',
      tasks: [],
      routineTasks: [],
      periodicTasks: [],
      inference: preEmergent,
      sub: {
        ...herbicideSub,
        quantity: preEmergent.totalAcres,
      },
    };
  }

  const irrigationIndex = serviceLines.findIndex(line => line.role === 'Irrigation');
  if (irrigationIndex >= 0 && irrigation.totalZones > 0 && serviceLines[irrigationIndex].tasks.length === 0) {
    const cfg = settings.irrigationZoneInference || DEFAULT_ESTIMATE_SETTINGS.irrigationZoneInference;
    const minutesPerZone = safeNumber(cfg.minutesPerZone, 12);
    const hoursPerVisit = irrigation.totalZones * minutesPerZone / 60; // full check, no *0.5
    // Irrigation is NOT a maintenance task -> NO seasonal heat factor.
    const irrigationTask = {
      serviceCode: 'irrigation_check',
      label: 'Irrigation Check',
      areaCount: irrigation.totalZones,
      ratePerHour: minutesPerZone > 0 ? 60 / minutesPerZone : 0,
      quantity: irrigation.totalZones,
      coverage: 1,
      frequency: 'monthly',
      hoursPerVisit,
      annualHours: hoursPerVisit * 12,
    };
    serviceLines[irrigationIndex] = {
      ...serviceLines[irrigationIndex],
      enabled: true,
      inference: irrigation,
      periodicTasks: [irrigationTask],
      tasks: [irrigationTask],
    };
  } else if (irrigationIndex >= 0) {
    serviceLines[irrigationIndex] = {
      ...serviceLines[irrigationIndex],
      inference: irrigation,
    };
  }

  return serviceLines;
}
